// src/pages/markets/board/card/OtherGroupBadge.tsx
//
// The group toggle is gone: a column owns its group, and switching group
// inside a card meant the card no longer belonged to the column that ranked it.
// What the toggle was actually used for — noticing that the *other* group is
// also positioned in this event — is a fact, not a navigation. So it is a
// badge: colour says which group, the number says in how many markets, and the
// reader goes to that column if they want the detail.

import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { EventConvictionIndex } from '../api/boardTypes';
import { useBoardGroups } from '../state/boardGroupsContext';
import { readableTone } from '../utils/readableTone';

export interface OtherGroupBadgeProps {
    index: EventConvictionIndex | undefined;
    // The group this card is showing; every other scored group gets a badge.
    activeGroup: string;
}

export function OtherGroupBadge({ index, activeGroup }: OtherGroupBadgeProps) {
    const groups = useBoardGroups();
    const theme = useTheme();
    const others = (index?.by_group ?? []).filter(
        g => g.group !== activeGroup && g.market_count > 0 && groups.offered(g.group),
    );

    if (others.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0 }}>
            {others.map(g => {
                const color = groups.resolve(g.group).color;
                const name = groups.titleOf(g.group);
                return (
                    <Tooltip
                        key={g.group}
                        title={`${name} is also positioned here — ${g.market_count} ${g.market_count === 1 ? 'market' : 'markets'
                            } (${g.yes_count} YES / ${g.no_count} NO)`}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.3,
                                px: 0.4,
                                borderRadius: '3px',
                                bgcolor: alpha(color, 0.16),
                                border: `1px solid ${alpha(color, 0.45)}`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    bgcolor: color,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    lineHeight: 1.4,
                                    color: readableTone(color, theme),
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {name}
                                {g.market_count > 1 ? ` ${g.market_count}` : ''}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}